"use client";
import { motion } from "framer-motion";
import { useTheme } from "next-themes";

export default function VenueInsights() {
  const { resolvedTheme } = useTheme();
  const isDark = resolvedTheme === "dark";

  return (
    <section 
      aria-labelledby="venue-insights-heading"
      className="venue-insights-section"
    >
      <h2 id="venue-insights-heading" className="sr-only">Venue Intelligence & Analytics</h2>
      <div className="relative w-full flex flex-col lg:flex-row items-center justify-between mt-24">
        <div className="border-0 border-yellow-800 w-full lg:w-1/2 p-5 lg:p-10">
          <motion.div
            initial={{ opacity: 0, x: 40 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ delay: 0.2, duration: 0.7, ease: "easeOut" }}
            className="relative flex items-center justify-center border-0 border-blue-700 p-5"
          >
            {/* Concert-themed animated elements */}
            <div className="absolute inset-0 flex items-center justify-center">
              {/* Animated stage lights */}
              <motion.div
                className="absolute w-96 h-96"
                animate={{
                  rotate: [0, 360],
                }}
                transition={{
                  duration: 20,
                  repeat: Infinity,
                  ease: "linear",
                }}
              >
                <svg width="100%" height="100%" viewBox="0 0 400 400" fill="none" xmlns="http://www.w3.org/2000/svg">
                  {/* Stage light beams */}
                  <motion.path
                    d="M200 50 L150 200 L250 200 Z"
                    fill="url(#stageLight1)"
                    animate={{
                      opacity: [0.3, 1, 0.3],
                      scale: [0.8, 1.2, 0.8],
                    }}
                    transition={{
                      duration: 3,
                      repeat: Infinity,
                      ease: "easeInOut",
                    }}
                  />
                  <motion.path
                    d="M200 50 L100 250 L300 250 Z"
                    fill="url(#stageLight2)"
                    animate={{
                      opacity: [0.3, 1, 0.3],
                      scale: [0.8, 1.2, 0.8],
                    }}
                    transition={{
                      duration: 3,
                      repeat: Infinity,
                      ease: "easeInOut",
                      delay: 1,
                    }}
                  />
                  <motion.path
                    d="M200 50 L50 300 L350 300 Z"
                    fill="url(#stageLight3)"
                    animate={{
                      opacity: [0.3, 1, 0.3],
                      scale: [0.8, 1.2, 0.8],
                    }}
                    transition={{
                      duration: 3,
                      repeat: Infinity,
                      ease: "easeInOut",
                      delay: 2,
                    }}
                  />
                  <defs>
                    <linearGradient id="stageLight1" x1="0%" y1="0%" x2="100%" y2="100%">
                      <stop offset="0%" stopColor="#FEC941" stopOpacity="0.8" />
                      <stop offset="100%" stopColor="#FEC941" stopOpacity="0" />
                    </linearGradient>
                    <linearGradient id="stageLight2" x1="0%" y1="0%" x2="100%" y2="100%">
                      <stop offset="0%" stopColor="#ff00c8" stopOpacity="0.8" />
                      <stop offset="100%" stopColor="#ff00c8" stopOpacity="0" />
                    </linearGradient>
                    <linearGradient id="stageLight3" x1="0%" y1="0%" x2="100%" y2="100%">
                      <stop offset="0%" stopColor="#2485EC" stopOpacity="0.8" />
                      <stop offset="100%" stopColor="#2485EC" stopOpacity="0" />
                    </linearGradient>
                  </defs>
                </svg>
              </motion.div>

              {/* Animated crowd silhouettes */}
              <motion.div
                className="absolute w-80 h-80"
                animate={{
                  rotate: [0, -360],
                }}
                transition={{
                  duration: 25,
                  repeat: Infinity,
                  ease: "linear",
                }}
              >
                <svg width="100%" height="100%" viewBox="0 0 320 320" fill="none" xmlns="http://www.w3.org/2000/svg">
                  {/* Crowd figures */}
                  <motion.path
                    d="M80 280 L90 260 L100 280 L110 260 L120 280"
                    stroke="url(#crowdGradient)"
                    strokeWidth="2"
                    fill="none"
                    animate={{
                      opacity: [0.2, 0.8, 0.2],
                    }}
                    transition={{
                      duration: 4,
                      repeat: Infinity,
                      ease: "easeInOut",
                    }}
                  />
                  <motion.path
                    d="M200 280 L210 260 L220 280 L230 260 L240 280"
                    stroke="url(#crowdGradient)"
                    strokeWidth="2"
                    fill="none"
                    animate={{
                      opacity: [0.2, 0.8, 0.2],
                    }}
                    transition={{
                      duration: 4,
                      repeat: Infinity,
                      ease: "easeInOut",
                      delay: 1,
                    }}
                  />
                  <motion.path
                    d="M140 280 L150 260 L160 280 L170 260 L180 280"
                    stroke="url(#crowdGradient)"
                    strokeWidth="2"
                    fill="none"
                    animate={{
                      opacity: [0.2, 0.8, 0.2],
                    }}
                    transition={{
                      duration: 4,
                      repeat: Infinity,
                      ease: "easeInOut",
                      delay: 2,
                    }}
                  />
                  <defs>
                    <linearGradient id="crowdGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                      <stop offset="0%" stopColor="#2485EC" stopOpacity="0.6" />
                      <stop offset="100%" stopColor="#2485EC" stopOpacity="0.2" />
                    </linearGradient>
                  </defs>
                </svg>
              </motion.div>

              {/* Animated sound waves */}
              <motion.div
                className="absolute w-64 h-64"
                animate={{
                  rotate: [360, 0],
                }}
                transition={{
                  duration: 30,
                  repeat: Infinity,
                  ease: "linear",
                }}
              >
                <svg width="100%" height="100%" viewBox="0 0 256 256" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <motion.path
                    d="M128 50 L128 80 M128 90 L128 120 M128 130 L128 160"
                    stroke="url(#soundWaveGradient)"
                    strokeWidth="3"
                    strokeLinecap="round"
                    animate={{
                      scaleY: [1, 1.5, 1],
                      opacity: [0.3, 1, 0.3],
                    }}
                    transition={{
                      duration: 2,
                      repeat: Infinity,
                      ease: "easeInOut",
                    }}
                  />
                  <motion.path
                    d="M128 170 L128 200 M128 210 L128 240"
                    stroke="url(#soundWaveGradient)"
                    strokeWidth="3"
                    strokeLinecap="round"
                    animate={{
                      scaleY: [1, 1.5, 1],
                      opacity: [0.3, 1, 0.3],
                    }}
                    transition={{
                      duration: 2,
                      repeat: Infinity,
                      ease: "easeInOut",
                      delay: 0.5,
                    }}
                  />
                  <defs>
                    <linearGradient id="soundWaveGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                      <stop offset="0%" stopColor="#FEC941" stopOpacity="0.8" />
                      <stop offset="100%" stopColor="#ff00c8" stopOpacity="0.8" />
                    </linearGradient>
                  </defs>
                </svg>
              </motion.div>
            </div>
            
            {/* Venue Dashboard SVG */}
            <div className="relative w-3/4 h-80 flex items-center justify-center z-10">
              <svg width="100%" height="100%" viewBox="0 0 400 300" fill="none" xmlns="http://www.w3.org/2000/svg" className="drop-shadow-lg">
                {/* Dashboard Background */}
                <rect x="20" y="20" width="360" height="260" rx="12" fill="url(#venueDashboardBg)" stroke="url(#venueDashboardBorder)" strokeWidth="2"/>
                
                {/* Header */}
                <rect x="40" y="40" width="320" height="40" rx="8" fill="url(#venueHeaderGradient)"/>
                <text x="60" y="65" fill="white" fontSize="14" fontWeight="600"> Venue Insights</text>
                
                {/* Main Content Area */}
                <rect x="40" y="100" width="320" height="160" rx="8" fill="url(#venueChartBg)" stroke="url(#venueChartBorder)" strokeWidth="1"/>
                
                {/* Stage Layout */}
                <rect x="60" y="120" width="120" height="60" rx="4" fill="url(#stageGradient)" stroke="url(#stageBorder)" strokeWidth="1"/>
                <text x="70" y="140" fill="white" fontSize="10" fontWeight="600">STAGE</text>
                
                {/* Seating Chart */}
                <motion.rect
                  x="200" y="120" width="140" height="60" rx="4" fill="url(#seatingGradient)"
                  animate={{
                    opacity: [0.7, 1, 0.7],
                  }}
                  transition={{
                    duration: 3,
                    repeat: Infinity,
                    ease: "easeInOut",
                  }}
                />
                <text x="210" y="140" fill="white" fontSize="10" fontWeight="600">CAPACITY</text>
                
                {/* Performance Metrics */}
                <rect x="60" y="200" width="80" height="40" rx="4" fill="url(#metricGradient1)"/>
                <text x="70" y="220" fill="white" fontSize="8" fontWeight="600">SOLD OUT</text>
                <text x="70" y="230" fill="white" fontSize="12" fontWeight="bold">95%</text>
                
                <rect x="150" y="200" width="80" height="40" rx="4" fill="url(#metricGradient2)"/>
                <text x="160" y="220" fill="white" fontSize="8" fontWeight="600">ENGAGEMENT</text>
                <text x="160" y="230" fill="white" fontSize="12" fontWeight="bold">4.8★</text>
                
                <rect x="240" y="200" width="80" height="40" rx="4" fill="url(#metricGradient3)"/>
                <text x="250" y="220" fill="white" fontSize="8" fontWeight="600">REVENUE</text>
                <text x="250" y="230" fill="white" fontSize="12" fontWeight="bold">$12K</text>
                
                {/* Talent Discovery Section */}
                <rect x="320" y="120" width="40" height="40" rx="4" fill="url(#talentGradient)"/>
                <text x="325" y="135" fill="white" fontSize="6" fontWeight="600">TALENT</text>
                <text x="325" y="145" fill="white" fontSize="8" fontWeight="bold">FIND</text>
                
                {/* Content Hub */}
                <rect x="320" y="170" width="40" height="40" rx="4" fill="url(#contentGradient)"/>
                <text x="325" y="185" fill="white" fontSize="6" fontWeight="600">CONTENT</text>
                <text x="325" y="195" fill="white" fontSize="8" fontWeight="bold">HUB</text>
                
                {/* Animated crowd dots */}
                <motion.circle
                  cx="80" cy="150" r="2"
                  fill="#FEC941"
                  animate={{
                    opacity: [0.3, 1, 0.3],
                    scale: [0.5, 1.5, 0.5],
                  }}
                  transition={{
                    duration: 2,
                    repeat: Infinity,
                    ease: "easeInOut",
                  }}
                />
                <motion.circle
                  cx="100" cy="150" r="2"
                  fill="#ff00c8"
                  animate={{
                    opacity: [0.3, 1, 0.3],
                    scale: [0.5, 1.5, 0.5],
                  }}
                  transition={{
                    duration: 2,
                    repeat: Infinity,
                    ease: "easeInOut",
                    delay: 0.3,
                  }}
                />
                <motion.circle
                  cx="120" cy="150" r="2"
                  fill="#2485EC"
                  animate={{
                    opacity: [0.3, 1, 0.3],
                    scale: [0.5, 1.5, 0.5],
                  }}
                  transition={{
                    duration: 2,
                    repeat: Infinity,
                    ease: "easeInOut",
                    delay: 0.6,
                  }}
                />
                
                {/* Definitions */}
                <defs>
                  <linearGradient id="venueDashboardBg" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" stopColor="rgba(255,255,255,0.1)" />
                    <stop offset="100%" stopColor="rgba(255,255,255,0.05)" />
                  </linearGradient>
                  <linearGradient id="venueDashboardBorder" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" stopColor="#FEC941" stopOpacity="0.3" />
                    <stop offset="100%" stopColor="#FEC941" stopOpacity="0.1" />
                  </linearGradient>
                  <linearGradient id="venueHeaderGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                    <stop offset="0%" stopColor="#FEC941" />
                    <stop offset="100%" stopColor="#FEC941" stopOpacity="0.8" />
                  </linearGradient>
                  <linearGradient id="venueChartBg" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" stopColor="rgba(255,255,255,0.05)" />
                    <stop offset="100%" stopColor="rgba(255,255,255,0.02)" />
                  </linearGradient>
                  <linearGradient id="venueChartBorder" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" stopColor="#FEC941" stopOpacity="0.2" />
                    <stop offset="100%" stopColor="#FEC941" stopOpacity="0.1" />
                  </linearGradient>
                  <linearGradient id="stageGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" stopColor="#ff00c8" stopOpacity="0.8" />
                    <stop offset="100%" stopColor="#ff00c8" stopOpacity="0.4" />
                  </linearGradient>
                  <linearGradient id="stageBorder" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" stopColor="#ff00c8" stopOpacity="0.6" />
                    <stop offset="100%" stopColor="#ff00c8" stopOpacity="0.3" />
                  </linearGradient>
                  <linearGradient id="seatingGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" stopColor="#2485EC" stopOpacity="0.6" />
                    <stop offset="100%" stopColor="#2485EC" stopOpacity="0.3" />
                  </linearGradient>
                  <linearGradient id="talentGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" stopColor="#FEC941" stopOpacity="0.8" />
                    <stop offset="100%" stopColor="#FEC941" stopOpacity="0.4" />
                  </linearGradient>
                  <linearGradient id="contentGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" stopColor="#ff00c8" stopOpacity="0.8" />
                    <stop offset="100%" stopColor="#ff00c8" stopOpacity="0.4" />
                  </linearGradient>
                </defs>
              </svg>
            </div>
          </motion.div>
        </div>

        <div className="border-0 border-blue-700 w-full lg:w-1/2 p-5 lg:p-10">
          <motion.div
            initial={{ opacity: 0, x: -40 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ delay: 0.1, duration: 0.7, ease: "easeOut" }}
            className="wow fadeInUp lg:max-w-[510px]"
          >
            <span className="mb-4 block text-lg font-medium text-primary md:text-[22px]">For Venues</span>
            <h2 className="mb-4 capitalize text-3xl font-bold text-black dark:text-white sm:text-4xl md:text-[44px] md:leading-tight">Venue Intelligence</h2>
            <p className="mb-[30px] text-base leading-relaxed text-black dark:text-white/80">
              Transform your venue into a data-driven powerhouse. Get insights into crowd engagement, discover emerging talent, and access a treasure trove of promotional content. Make informed decisions that drive ticket sales and create unforgettable experiences.
            </p>
            <motion.div
              className="mb-[30px] flex flex-col sm:flex-row items-start sm:items-center"
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true, amount: 0.3 }}
              transition={{ delay: 0.4, duration: 0.6, ease: "easeOut" }}
            >
              <div className={`mb-4 sm:mb-0 mr-0 sm:mr-[22px] flex h-[60px] w-[60px] items-center justify-center rounded-full border border-stroke text-xl font-semibold ${isDark ? 'text-white bg-white/10 border-white/30' : 'text-black bg-black/10 border-black/20'}`}>01</div>
              <div>
                <h5 className={`text-xl font-medium ${isDark ? 'text-white' : 'text-black'}`}>Talent Discovery</h5>
                <p className={`text-base ${isDark ? 'text-white/70' : 'text-black/70'}`}>Find emerging artists and trending performers in your area.</p>
              </div>
            </motion.div>
            <motion.div
              className="flex flex-col sm:flex-row items-start sm:items-center"
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true, amount: 0.3 }}
              transition={{ delay: 0.5, duration: 0.6, ease: "easeOut" }}
            >
              <div className={`mb-4 sm:mb-0 mr-0 sm:mr-[22px] flex h-[60px] w-[60px] items-center justify-center rounded-full border border-stroke text-xl font-semibold ${isDark ? 'text-white bg-white/10 border-white/30' : 'text-black bg-black/10 border-black/20'}`}>02</div>
              <div>
                <h5 className={`text-xl font-medium ${isDark ? 'text-white' : 'text-black'}`}>Promotional Content</h5>
                <p className={`text-base ${isDark ? 'text-white/70' : 'text-black/70'}`}>Access high-quality clips and content for marketing campaigns.</p>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
} 